import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Avatar from '../components/Avatar';
import { isAdmin } from '../utils/role';

const ROLE_OPTIONS = ['TEAM_LEAD', 'DEVELOPER', 'MEMBER'];

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ teamName: '', companyId: '', teamLeadId: '', teamLeadUsername: '' });
  const [error, setError] = useState('');
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [members, setMembers] = useState({});
  const [showAddMember, setShowAddMember] = useState(null);
  const [memberForm, setMemberForm] = useState({ userId: '', roleInTeam: 'DEVELOPER' });

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const load = async () => {
    try {
      const [t, c, u] = await Promise.all([api.get('/teams'), api.get('/companies').catch(() => ({ data: [] })), api.get('/users')]);
      setTeams(t.data);
      setCompanies(c.data);
      setUsers(u.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const loadMembers = async (teamId) => {
    try {
      const r = await api.get(`/teams/${teamId}/members`);
      setMembers(prev => ({ ...prev, [teamId]: r.data }));
    } catch {}
  };

  const toggleExpand = (teamId) => {
    if (expandedTeam === teamId) {
      setExpandedTeam(null);
    } else {
      setExpandedTeam(teamId);
      loadMembers(teamId);
    }
  };

  const getCompanyName = (id) => { const c = companies.find(c => c.id === id); return c ? c.name : '-'; };
  const getUserById = (id) => users.find(u => u.id === id);

  const openNew = () => { setForm({ teamName: '', companyId: '', teamLeadId: '', teamLeadUsername: '' }); setEditing(null); setShowForm(true); setError(''); };
  const openEdit = (t) => { setForm({ teamName: t.teamName, companyId: t.companyId || '', teamLeadId: t.teamLeadId || '', teamLeadUsername: t.teamLeadUsername || '' }); setEditing(t.id); setShowForm(true); setError(''); };

  const save = async () => {
    setError('');
    if (!form.teamName || !form.companyId) { setError('Team Name and Company are required'); return; }
    const lead = users.find(u => u.id === Number(form.teamLeadId));
    const payload = { ...form, companyId: Number(form.companyId), teamLeadId: form.teamLeadId ? Number(form.teamLeadId) : null, teamLeadUsername: lead ? lead.username : '' };
    try {
      if (editing) { await api.put(`/teams/${editing}`, payload); }
      else { await api.post('/teams', payload); }
      setShowForm(false); setEditing(null); load();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
  };

  const deleteTeam = async (id) => {
    if (!window.confirm('Delete this team and all its members?')) return;
    try {
      await api.delete(`/teams/${id}`);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Failed to delete'); }
  };

  const addMember = async (teamId) => {
    if (!memberForm.userId) return;
    const user = users.find(u => u.id === Number(memberForm.userId));
    if (!user) return;
    try {
      await api.post(`/teams/${teamId}/members`, {
        userId: user.id, username: user.username, fullName: user.fullName, roleInTeam: memberForm.roleInTeam
      });
      setShowAddMember(null); setMemberForm({ userId: '', roleInTeam: 'DEVELOPER' });
      loadMembers(teamId);
    } catch (e) { alert(e.response?.data?.error || 'Failed to add member'); }
  };

  const removeMember = async (teamId, memberId) => {
    if (!window.confirm('Remove this member from the team?')) return;
    try {
      await api.delete(`/teams/${teamId}/members/${memberId}`);
      loadMembers(teamId);
    } catch (e) { alert(e.response?.data?.error || 'Failed to remove'); }
  };

  const teamMembers = (teamId) => members[teamId] || [];
  const activeMembers = (teamId) => teamMembers(teamId).filter(m => m.status === 'ACTIVE');
  const existingUserIds = (teamId) => teamMembers(teamId).filter(m => m.status === 'ACTIVE').map(m => m.userId);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>Teams</h2>
        <button className="btn-primary" onClick={openNew}>+ New Team</button>
      </div>

      {/* Team Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontWeight: 700, marginBottom: 20, color: '#0f172a' }}>{editing ? 'Edit Team' : 'New Team'}</h3>
            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Company *</label>
                <select value={form.companyId} onChange={e => setForm({ ...form, companyId: e.target.value })}>
                  <option value="">Select Company</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Team Name *</label>
                <input value={form.teamName} onChange={e => setForm({ ...form, teamName: e.target.value })} placeholder="e.g. Java Team" />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Team Lead</label>
                <select value={form.teamLeadId} onChange={e => setForm({ ...form, teamLeadId: e.target.value })}>
                  <option value="">Select Team Lead</option>
                  {users.filter(u => u.active).map(u => <option key={u.id} value={u.id}>{u.fullName} ({u.username})</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button className="btn-primary" onClick={save} style={{ flex: 1 }}>{editing ? 'Update' : 'Create'}</button>
              {editing && (isAdmin() || (() => { const t = teams.find(t => t.id === editing); return t && t.createdBy === currentUser.username; })()) && <button className="btn-danger" onClick={() => { deleteTeam(editing); setShowForm(false); }} style={{ padding: '10px 20px' }}>Delete</button>}
              <button className="btn-ghost" onClick={() => { setShowForm(false); setEditing(null); }} style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Teams List */}
      {teams.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
          <div>No teams yet. Create your first team!</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {teams.map(t => {
            const lead = t.teamLeadId ? getUserById(t.teamLeadId) : null;
            const isExpanded = expandedTeam === t.id;
            const mems = activeMembers(t.id);
            return (
              <div key={t.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', cursor: 'pointer' }} onClick={() => toggleExpand(t.id)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 20 }}>👥</span>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{t.teamName}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {getCompanyName(t.companyId)}
                        {lead && <span> · Lead: {lead.fullName}</span>}
                        {isExpanded && <span> · {mems.length} member{mems.length !== 1 ? 's' : ''}</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span className={`badge ${t.status === 'ACTIVE' ? 'badge-success' : 'badge-gray'}`}>{t.status}</span>
                    <button className="btn-ghost" onClick={(e) => { e.stopPropagation(); openEdit(t); }} style={{ padding: '4px 10px', fontSize: 12 }}>Edit</button>
                    <span style={{ fontSize: 14, color: '#9ca3af', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ borderTop: '1px solid #e5e7eb', padding: '16px 20px', background: '#f9fafb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Members ({mems.length})</span>
                      <button className="btn-primary" onClick={() => { setShowAddMember(t.id); setMemberForm({ userId: '', roleInTeam: 'DEVELOPER' }); }} style={{ fontSize: 12, padding: '4px 12px' }}>+ Add Member</button>
                    </div>

                    {/* Add Member Form */}
                    {showAddMember === t.id && (
                      <div style={{ background: '#fff', borderRadius: 8, padding: 14, marginBottom: 12, border: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>User</label>
                            <select value={memberForm.userId} onChange={e => setMemberForm({ ...memberForm, userId: e.target.value })} style={{ fontSize: 12 }}>
                              <option value="">Select User</option>
                              {users.filter(u => u.active && !existingUserIds(t.id).includes(u.id)).map(u => (
                                <option key={u.id} value={u.id}>{u.fullName} ({u.username})</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ width: 140 }}>
                            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Role</label>
                            <select value={memberForm.roleInTeam} onChange={e => setMemberForm({ ...memberForm, roleInTeam: e.target.value })} style={{ fontSize: 12 }}>
                              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                          </div>
                          <button className="btn-primary" onClick={() => addMember(t.id)} style={{ fontSize: 12, padding: '6px 14px' }}>Add</button>
                          <button className="btn-ghost" onClick={() => setShowAddMember(null)} style={{ fontSize: 12, padding: '6px 10px' }}>Cancel</button>
                        </div>
                      </div>
                    )}

                    {/* Members List */}
                    {mems.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 16, color: '#9ca3af', fontSize: 13 }}>No members yet</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 6 }}>
                        {mems.map(m => (
                          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                            <Avatar name={m.fullName} size={32} fontSize={13} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{m.fullName}</div>
                              <div style={{ fontSize: 11, color: '#6b7280' }}>{m.username}</div>
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: m.roleInTeam === 'TEAM_LEAD' ? '#fef3c7' : m.roleInTeam === 'DEVELOPER' ? '#dbeafe' : '#f3f4f6', color: m.roleInTeam === 'TEAM_LEAD' ? '#92400e' : m.roleInTeam === 'DEVELOPER' ? '#1d4ed8' : '#374151' }}>
                              {m.roleInTeam}
                            </span>
                            {(isAdmin() || m.createdBy === currentUser.username) && <button className="btn-ghost" onClick={() => removeMember(t.id, m.id)} style={{ padding: '2px 8px', fontSize: 11, color: '#dc2626' }}>Remove</button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
